<script>
/**
 * BrandLogo -- the single renderer for the platform brand mark.
 *
 * Presentation-only: the parent supplies the signed `src` (authed surfaces
 * read it from `state.admin.core.brand_logo_src`; public surfaces fetch it
 * from `auth/branding`). This component never touches Vuex or fetches itself,
 * so it works identically on authed and unauthenticated pages (FR-5, UX-DR6).
 *
 * It owns ONE universal concern: never show a broken image. A signed S3 URL
 * can be null, expired, or dangling (404 on load) -- in every case a guarded
 * native `@error` swaps to the bundled static `fallback` and emits `refetch`
 * so the surface can request a fresh signed URL. Two guards keep this safe:
 *   1. `errored` stops the fallback swap from re-triggering the handler.
 *   2. `refetchCount` caps `refetch` emissions at MAX_REFETCH, so a genuinely
 *      dangling/404 key (each `refetch` yields a NEW signed URL, so the `src`
 *      watch keeps re-arming) cannot spin an unbounded error->refetch loop
 *      (on authed surfaces every loop iteration reloads core). A successful
 *      `@load` of a real signed URL resets the budget so later expiries recover.
 *
 * Signed URLs expire (300s TTL) -- never persist `src` here.
 */
const MAX_REFETCH = 2;

export default {
  name: "BrandLogo",
  props: {
    // The signed S3 URL. Null/undefined (no logo, or pre-core render) -> fallback.
    src: {
      type: String,
      default: null
    },
    // Resolved static asset URL (e.g. require("@/assets/images/tfp_logo.png")).
    // Bundled, so it can never fail to load -- the safe terminal state.
    fallback: {
      type: String,
      required: true
    },
    // Meaningful, non-empty alt text (e.g. "TFP") -- accessibility (UX-DR6).
    alt: {
      type: String,
      required: true
    },
    // Optional sizing pass-throughs so each surface keeps its current dimensions.
    height: {
      type: [String, Number],
      default: null
    },
    imgClass: {
      type: String,
      default: null
    }
  },
  data() {
    return { errored: false, refetchCount: 0 };
  },
  computed: {
    resolvedSrc() {
      return this.errored || !this.src ? this.fallback : this.src;
    }
  },
  watch: {
    // A fresh signed URL arrived (parent re-fetched / core reloaded) -- try it
    // again. Do NOT reset refetchCount here: each refetch changes `src`, so
    // resetting the budget on every change would defeat the loop cap. The
    // budget is only refilled by a genuinely successful `@load` (onLoad).
    src() {
      this.errored = false;
    }
  },
  methods: {
    onError() {
      if (this.errored) return; // guard: fallback is bundled & can't fail -> no infinite loop
      this.errored = true; // swap to static fallback (never a broken image)
      if (this.refetchCount < MAX_REFETCH) {
        this.refetchCount += 1;
        this.$emit("refetch"); // ask the surface for a fresh signed URL (bounded)
      }
      // budget spent -> a persistently dangling/404 key settles on the fallback
      // instead of looping refetch (and, on authed surfaces, reloading core).
    },
    onLoad() {
      // A real signed URL loaded successfully -- refill the retry budget so a
      // later 300s expiry can recover. Ignore fallback loads (errored / no src).
      if (this.errored || !this.src) return;
      this.refetchCount = 0;
    }
  }
};
</script>

<template>
  <img
    :src="resolvedSrc"
    :alt="alt"
    :height="height"
    :class="imgClass"
    @error="onError"
    @load="onLoad"
  />
</template>

<style scoped></style>
